import React from 'react';
import { Box } from '@mui/material';
import SectionCard from './SectionCard';

/**
 * Single draggable wrapper around a SectionCard inside one region.
 *
 * Owns its HTML5 drag-and-drop wiring; reorder math lives in the
 * parent (which knows the full ordered list for this region).
 *
 * `drag` is the shared {region, draggedId, overId} state from the
 * parent — kept on the parent so cross-card hover effects work.
 */
export default function DraggableSection({
  section, region, drag, setDrag,
  onReorder,
  catalog, brandId,
  onPatch, onDelete, onComponentChange,
}) {
  const isDragged = drag.draggedId === section._id && drag.region === region;
  const isOver    = drag.overId    === section._id && drag.region === region;

  return (
    <Box
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', section._id); } catch (_) { /* ignore */ }
        setDrag({ region, draggedId: section._id, overId: null });
      }}
      onDragOver={(e) => {
        if (drag.region !== region || !drag.draggedId || drag.draggedId === section._id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (drag.overId !== section._id) {
          setDrag((s) => ({ ...s, overId: section._id }));
        }
      }}
      onDragLeave={() => {
        if (drag.overId === section._id) setDrag((s) => ({ ...s, overId: null }));
      }}
      onDrop={(e) => {
        e.preventDefault();
        const draggedId = drag.draggedId;
        if (!draggedId || draggedId === section._id || drag.region !== region) {
          setDrag({ region: null, draggedId: null, overId: null });
          return;
        }
        onReorder(draggedId, section._id);
        setDrag({ region: null, draggedId: null, overId: null });
      }}
      onDragEnd={() => setDrag({ region: null, draggedId: null, overId: null })}
      sx={{
        opacity: isDragged ? 0.4 : 1,
        borderTop: isOver ? '3px solid' : '3px solid transparent',
        borderTopColor: isOver ? 'primary.main' : 'transparent',
        transition: 'opacity 120ms ease',
      }}
    >
      <SectionCard
        section={section}
        catalog={catalog}
        brandId={brandId}
        onPatch={onPatch}
        onDelete={onDelete}
        onComponentChange={onComponentChange}
      />
    </Box>
  );
}
